import React, { useEffect, useState } from "react";

const LoadingScreen: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [phase, setPhase] = useState<"in" | "hold" | "out">("in");

  useEffect(() => {
    // Phase: in (0–800ms) → hold (800–2000ms) → out (2000–2700ms) → done
    const holdTimer = setTimeout(() => setPhase("hold"), 800);
    const outTimer  = setTimeout(() => setPhase("out"),  2000);
    const doneTimer = setTimeout(() => onDone(),          2700);
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(outTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <>
      <style>{`
        @keyframes ls-shield-pop {
          0%   { transform: scale(0.6) translateY(12px); opacity: 0; }
          70%  { transform: scale(1.05) translateY(-2px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes ls-ring {
          0%   { transform: scale(0.7); opacity: 0; }
          60%  { transform: scale(1.08); opacity: 0.25; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        @keyframes ls-scan {
          0%, 100% { transform: translateY(-28px); opacity: 0.4; }
          50%       { transform: translateY(28px);  opacity: 1; }
        }
        @keyframes ls-text-in {
          from { opacity: 0; letter-spacing: 0.3em; }
          to   { opacity: 1; letter-spacing: 0.12em; }
        }
        @keyframes ls-bar {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes ls-fade-out {
          from { opacity: 1; transform: scale(1); }
          to   { opacity: 0; transform: scale(1.04); }
        }
        @keyframes ls-dot-pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50%       { opacity: 1;   transform: scale(1.2); }
        }
        .ls-root {
          position: fixed; inset: 0; z-index: 99999;
          background: #ffffff;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .ls-root.out {
          opacity: 0;
          transform: scale(1.04);
          pointer-events: none;
        }
        .ls-shield-wrap {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 32px;
        }
        .ls-ring {
          position: absolute;
          width: 140px; height: 140px;
          border-radius: 50%;
          border: 2px solid #00c896;
          animation: ls-ring 2s ease-out infinite;
        }
        .ls-ring:nth-child(2) { animation-delay: 0.6s; }
        .ls-ring:nth-child(3) { animation-delay: 1.2s; }
        .ls-shield {
          animation: ls-shield-pop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          position: relative; z-index: 2;
        }
        .ls-scan-line {
          position: absolute;
          left: 50%; transform: translateX(-50%);
          width: 56px; height: 2px;
          background: linear-gradient(90deg, transparent, #00c896, transparent);
          border-radius: 2px;
          animation: ls-scan 1.6s ease-in-out infinite;
          z-index: 3;
          animation-delay: 0.6s;
        }
        .ls-wordmark {
          color: #0f2040;
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-family: system-ui, -apple-system, sans-serif;
          animation: ls-text-in 0.6s 0.5s both;
          margin-bottom: 6px;
        }
        .ls-wordmark span { color: #00c896; }
        .ls-sub {
          color: rgba(15,32,64,0.4);
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-family: system-ui, sans-serif;
          animation: ls-text-in 0.6s 0.7s both;
          margin-bottom: 40px;
        }
        .ls-bar-track {
          width: 180px; height: 2px;
          background: rgba(15,32,64,0.08);
          border-radius: 2px;
          overflow: hidden;
          margin-bottom: 20px;
        }
        .ls-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #00c896, #3b82f6);
          border-radius: 2px;
          animation: ls-bar 1.8s 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        .ls-dots {
          display: flex; gap: 6px;
        }
        .ls-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: #00c896;
          animation: ls-dot-pulse 1.2s ease-in-out infinite;
        }
        .ls-dot:nth-child(2) { animation-delay: 0.2s; }
        .ls-dot:nth-child(3) { animation-delay: 0.4s; }
      `}</style>

      <div className={`ls-root${phase === "out" ? " out" : ""}`}>
        <div className="ls-shield-wrap">
          {/* Ripple rings */}
          <div className="ls-ring" />
          <div className="ls-ring" />
          <div className="ls-ring" />

          {/* Scan line */}
          <div className="ls-scan-line" />

          {/* Logo image */}
          <img
            className="ls-shield"
            src="/images/logo-shield-nobg.png"
            alt="TrustEngine"
            style={{ width: 100, height: 100, objectFit: "contain" }}
          />
        </div>

        {/* Wordmark */}
        <div className="ls-wordmark">
          Trust<span>Engine</span>
        </div>
        <div className="ls-sub">Identity Verification Platform</div>

        {/* Progress bar */}
        <div className="ls-bar-track">
          <div className="ls-bar-fill" />
        </div>

        {/* Dots */}
        <div className="ls-dots">
          <div className="ls-dot" />
          <div className="ls-dot" />
          <div className="ls-dot" />
        </div>
      </div>
    </>
  );
};

export default LoadingScreen;
